import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PetContext = createContext(null);

export function usePet() {
  const context = useContext(PetContext);
  if (!context) {
    throw new Error('usePet must be used within a PetProvider');
  }
  return context;
}

export function PetProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [pet, setPet] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPet = useCallback(async () => {
    if (!isAuthenticated) {
      setPet(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/pets/my-pet`, { withCredentials: true });
      setPet(data);
    } catch (e) {
      setPet(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchPet();
  }, [fetchPet]);

  const createPet = async (petType, name) => {
    try {
      const { data } = await axios.post(
        `${API}/pets`,
        { pet_type: petType, name },
        { withCredentials: true }
      );
      setPet(data);
      return { success: true, pet: data };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || e.message };
    }
  };

  const feedPet = async () => {
    try {
      await axios.post(`${API}/pets/feed`, {}, { withCredentials: true });
      await fetchPet();
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || e.message };
    }
  };

  const playWithPet = async () => {
    try {
      await axios.post(`${API}/pets/play`, {}, { withCredentials: true });
      await fetchPet();
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || e.message };
    }
  };

  const value = {
    pet,
    loading,
    createPet,
    feedPet,
    playWithPet,
    refreshPet: fetchPet,
    hasPet: !!pet,
  };

  return <PetContext.Provider value={value}>{children}</PetContext.Provider>;
}
